/**
 * Toast API — imperative helpers that write to the Zustand store.
 *
 * @example
 * import { toast } from '@/lib/toast'
 * toast.success('Sparat!')
 * toast.error('Något gick fel', { description: 'Försök igen.' })
 */

import { useToastStore, type ToastType } from './toastStore'
import { undoable } from './undo'

interface ToastOptions {
  description?: string
  /** Duration in ms. Defaults: success/info/warning=4000, error=6000 */
  durationMs?: number
}

const DEFAULTS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  warning: 4000,
  error: 6000,
}

function show(type: ToastType, title: string, opts?: ToastOptions) {
  useToastStore.getState().push({
    type,
    title,
    description: opts?.description,
    durationMs: opts?.durationMs ?? DEFAULTS[type],
  })
}

export const toast = {
  success: (title: string, opts?: ToastOptions) => show('success', title, opts),
  error: (title: string, opts?: ToastOptions) => show('error', title, opts),
  info: (title: string, opts?: ToastOptions) => show('info', title, opts),
  warning: (title: string, opts?: ToastOptions) => show('warning', title, opts),
  /** Show a toast with an undo button. Action runs immediately; undo restores prior state. */
  undoable,
} as const

// Re-export store for UI components
export { useToastStore } from './toastStore'
export type { ToastMessage, ToastType } from './toastStore'
export { undoRegistry } from './undo'
